import { useToast, UseToastOptions, ToastId } from '@chakra-ui/react';
import { useCallback, useRef, useEffect } from 'react';

interface ToastQueueItem {
  id: ToastId;
  options: UseToastOptions;
}

interface UseLimitedToastProps {
  maxToasts: number;
}
/**
 * hook that limits the number of visible toasts to some number.
 * when new one comes in, the oldest is auto removed
 */
export const useLimitedToast = ({ maxToasts }: UseLimitedToastProps) => {
  const toast = useToast();
  const toastQueue = useRef<ToastQueueItem[]>([]);

  const addToast = useCallback((options: UseToastOptions) => {
    const toastId = toast(options);
    if (toastId) {

      const queueItem: ToastQueueItem = {
        id: toastId,
        options
      };
      toastQueue.current.push(queueItem);
      
      if (toastQueue.current.length > maxToasts) {
        const oldestToast = toastQueue.current.shift();
        if (oldestToast) {
          toast.close(oldestToast.id);
        }
      }
    }
    
    return toastId;
  }, [toast, maxToasts]);

  const closeToast = useCallback((toastId: ToastId) => {
    toast.close(toastId);
    toastQueue.current = toastQueue.current.filter(item => item.id !== toastId);
  }, [toast]);

  const closeAllToasts = useCallback(() => {
    toastQueue.current.forEach(item => {
      toast.close(item.id);
    });
    toastQueue.current = [];
  }, [toast]);

  useEffect(() => {
    return () => {
      closeAllToasts();
    };
  }, [closeAllToasts]);

  return {
    toast: addToast,
    closeToast,
    closeAllToasts,
  };
};
